import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { resourceStore, generateResourceId } from './resourceStore';
import type { ResourceDefinition } from '../types';

export function useResources(): ResourceDefinition[] {
  const cachedRef = useRef<ResourceDefinition[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = resourceStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, []);

  const getSnapshot = useCallback(() => {
    const newResources = resourceStore.getSnapshot().context.resources;
    if (cachedRef.current.length === newResources.length &&
        cachedRef.current.every((r, i) => {
          const n = newResources[i];
          return n && r.id === n.id && r.updatedAt === n.updatedAt;
        })) {
      return cachedRef.current;
    }
    cachedRef.current = newResources;
    return newResources;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useResourceById(resourceId: string): ResourceDefinition | undefined {
  const resources = useResources();
  return useMemo(() => resources.find(r => r.id === resourceId), [resources, resourceId]);
}

export function useResourcesForBoard(boardId: string): ResourceDefinition[] {
  const resources = useResources();
  return useMemo(() => resources.filter(r =>
    r.scope.type === 'global' ||
    (r.scope.type === 'board' && r.scope.boardId === boardId) ||
    (r.scope.type === 'canvas' && r.scope.boardId === boardId)
  ), [resources, boardId]);
}

export function useResourcesForCanvas(boardId: string): ResourceDefinition[] {
  const resources = useResources();
  return useMemo(() => resources.filter(r =>
    r.scope.type === 'global' ||
    (r.scope.type === 'board' && r.scope.boardId === boardId) ||
    (r.scope.type === 'canvas' && r.scope.boardId === boardId)
  ), [resources, boardId]);
}

export function useResourceActions() {
  return useMemo(() => ({
    addResource: (resource: ResourceDefinition) =>
      resourceStore.send({ type: 'resource.add', resource }),
    addNewResource: (name: string, scope: ResourceDefinition['scope']) => {
      const id = generateResourceId(name);
      const now = new Date().toISOString();
      const resource: ResourceDefinition = {
        id, name, scope,
        category: '',
        fields: [],
        color: '#6366f1',
        createdAt: now, updatedAt: now,
      };
      resourceStore.send({ type: 'resource.add', resource });
      return id;
    },
    updateResource: (resourceId: string, updates: Partial<Omit<ResourceDefinition, 'id' | 'createdAt'>>) =>
      resourceStore.send({ type: 'resource.update', resourceId, updates }),
    deleteResource: (resourceId: string) =>
      resourceStore.send({ type: 'resource.delete', resourceId }),
    resetResources: () =>
      resourceStore.send({ type: 'resources.reset' }),
    importResources: (resources: ResourceDefinition[]) =>
      resourceStore.send({ type: 'resources.import', resources }),
  }), []);
}
